/**
 * シナリオ生成の track レジストリ。
 *
 * track = 「1 回のシナリオ生成をフロントで追跡する単位」。生成キューの行 ID と同じ
 * クライアント生成 ID (`scenario-<timestamp>`) をキーにする
 * (docs/features/scenario-generation-detach-plan.md §3・§4.1)。
 *
 * ここに置く理由:
 *  - 生成ボタンの無効化判定 (= attached な track があるか) を唯一の正本にするため。
 *    以前は useIsMutating + 生成キューの polling 行という 2 系統で判定していたが、
 *    生成キューの ✕ で「切り離し」(detach) を行うと mutation は完了まで pending の
 *    ままなのでボタンが解放されない。
 *  - 生成キューウィンドウ (GenerationQueueWindow) が巨大な useScenarioGeneration に
 *    依存せず `detachTrack` だけを import できるようにするため。
 *
 * レジストリはメモリのみ (永続化しない)。リロードで消えるのは現行の inFlightShellFlow と
 * 同じ挙動で、detached な track は復帰キーを持たないので復帰もしない。
 * 「不明な clientJobId = attached 扱い」という規則があるため、**消し忘れより残し過ぎの方が
 * 安全**。`tryRelease` の条件が判定できないときは残す。
 */
import { useSyncExternalStore } from 'react';
import type { Scenario, ScenarioResponse } from './types';
import { SCENARIO_JOB_STORAGE_KEY } from './constants';
import { settleScenarioJob } from './utils';

/** 進行中のシェルフロー (ランダム生成) の記録。
 *
 *  lastResult / phase はマウントローカルなので、シェル別ポーリング中に別画面へ遷移して
 *  戻るとスケルトンタブが消える。復帰 effect がこの記録から表示 (placeholder + ロード済み
 *  本文) を復元する。全シェル確定 + 保留イベント消費で破棄する。 */
export type InFlightShellFlow = {
  jobId: string;
  resultMeta: Omit<ScenarioResponse, 'scenarios'>;
  placeholders: Scenario[];
  removedIds: Set<string>;
  /** ready になり本文ロード (または保留) 済みのシェル ID (ポーリング重複・再入での二重ロード防止) */
  loadedIds: Set<string>;
  /** 全シェル確定済み。保留イベントが残っている間は記録を保持し、消費後に破棄する。 */
  settled: boolean;
  /** 切り離し済み。以降 ready になっても画面へは反映せず、シナリオ一覧の無効化だけ行う。 */
  detached: boolean;
};

/** 完了後の後書き (frame_mode / 参考画像一覧) に使う、生成開始時に控えた値。
 *  完了時点の画面の値を使うと、切り離した後にユーザーが変えた設定を古いシナリオへ
 *  書いてしまうため (計画書 §4.3・§4.5)。 */
export type ScenarioPostWrite = {
  /** 現行 effectiveFrameMode と同じ導出 (reference / storyboard / lastFrameMode)。
   *  型は Scenario['frame_mode'] と揃える (そのまま DB へ書き、state にも入れるため)。 */
  frameMode: NonNullable<Scenario['frame_mode']>;
  /** 参考画像モードのときだけ持つ */
  referenceImageUrls?: { id: string; sourceUrl: string; thumbnailUrl: string }[];
};

export type ScenarioGenerationTrack = {
  /** 'scenario-<timestamp>' (生成キューの行 ID と同一) */
  clientJobId: string;
  /** generate-async の応答で確定。応答前は null */
  serverJobId: string | null;
  detached: boolean;
  kind: 'normal' | 'shell' | 'resume';
  /** シェルフローのみ */
  shellFlow?: InFlightShellFlow;
  /** 切り離し時に作業エリア側で行う処理 (シェルタブ除去等)。attached の間だけ登録 */
  onDetach?: () => void;
  postWrite: ScenarioPostWrite;
  /** 生成 (ポーリング) が終わった = 生成中判定から外れた */
  finished: boolean;
  /** この track を参照して実行中・処理待ちの数
   *  (handleReadyShell / 一括取り出し済みで未処理の保留イベント) */
  inFlight: number;
};

const tracks = new Map<string, ScenarioGenerationTrack>();
const listeners = new Set<() => void>();
// useSyncExternalStore の getSnapshot は同じ値を返し続ける必要があるためキャッシュする
// (track オブジェクトは in-place で書き換えるので、attached かどうかが変わった時だけ差し替わる)。
let attachedSnapshot: ScenarioGenerationTrack | null = null;

/** 保留イベント (useScenarioGeneration の pendingGenerationEvents) に、この track の
 *  イベントが残っているかを問い合わせる述語。循環 import を避けるため、
 *  useScenarioGeneration 側がモジュール読み込み時に登録する。
 *  未登録 = 判定できないので「残っている」に倒す (残し過ぎの方が安全)。 */
let pendingEventProbe: ((clientJobId: string) => boolean) | null = null;

export function setPendingEventProbe(probe: (clientJobId: string) => boolean): void {
  pendingEventProbe = probe;
}

function computeAttached(): ScenarioGenerationTrack | null {
  for (const track of tracks.values()) {
    if (!track.detached && !track.finished) return track;
  }
  return null;
}

function notify(): void {
  const next = computeAttached();
  if (next === attachedSnapshot) return;
  attachedSnapshot = next;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

function getAttachedSnapshot(): ScenarioGenerationTrack | null {
  return attachedSnapshot;
}

/** attached な track (作業エリアに結びついた生成)。不変条件: 最大 1 件。 */
export function getAttachedTrack(): ScenarioGenerationTrack | null {
  return computeAttached();
}

export function getTrack(clientJobId: string): ScenarioGenerationTrack | undefined {
  return tracks.get(clientJobId);
}

export function startTrack(init: {
  clientJobId: string;
  kind: ScenarioGenerationTrack['kind'];
  serverJobId?: string | null;
  postWrite: ScenarioPostWrite;
  onDetach?: () => void;
}): ScenarioGenerationTrack {
  // 想定外 (ボタン無効化をすり抜けた二重クリック等) の 2 件目。例外にはせず、
  // ユーザー操作の順序 (後勝ち) に合わせて既存 attached を切り離し扱いにする (§4.9)。
  const existing = computeAttached();
  if (existing && existing.clientJobId !== init.clientJobId) {
    console.error(
      '[scenarioGenerationTracks] attached な生成が既にあります。既存を切り離して新しい生成を attached にします:',
      existing.clientJobId, '->', init.clientJobId,
    );
    detachTrack(existing.clientJobId);
  }
  const track: ScenarioGenerationTrack = {
    clientJobId: init.clientJobId,
    serverJobId: init.serverJobId ?? null,
    detached: false,
    kind: init.kind,
    onDetach: init.onDetach,
    postWrite: init.postWrite,
    finished: false,
    inFlight: 0,
  };
  tracks.set(init.clientJobId, track);
  notify();
  return track;
}

/** 生成 (ポーリング) が終わったことを記録する。**レジストリからは消さない**。
 *  attached ならこの時点で生成ボタンが解放される。 */
export function finishTrack(clientJobId: string): void {
  const track = tracks.get(clientJobId);
  if (!track) return;
  track.finished = true;
  notify();
  tryReleaseTrack(clientJobId);
}

/** レジストリから削除する (条件判定なし)。通常は tryReleaseTrack 経由で呼ぶ。 */
export function releaseTrack(clientJobId: string): void {
  if (tracks.delete(clientJobId)) notify();
}

/** 参照が無くなった track を削除する。
 *  条件: finished && inFlight === 0 && 当該 clientJobId の保留イベントが無い (§4.1)。
 *  条件を満たさない限り detached の記録は残り続けるので、後から届いた保留イベント・
 *  ハンドラは必ず detached を見る。 */
export function tryReleaseTrack(clientJobId: string): void {
  const track = tracks.get(clientJobId);
  if (!track) return;
  if (!track.finished || track.inFlight > 0) return;
  // 判定できない (probe 未登録) ときは残す
  if (!pendingEventProbe || pendingEventProbe(clientJobId)) return;
  releaseTrack(clientJobId);
}

/** この track を参照するハンドラ・取り出し済み保留イベントの数を +1。 */
export function beginHandler(clientJobId: string): void {
  const track = tracks.get(clientJobId);
  if (!track) return;
  track.inFlight += 1;
}

/** beginHandler と対で -1 し、参照が無くなったら track を削除する。 */
export function endHandler(clientJobId: string): void {
  const track = tracks.get(clientJobId);
  if (!track) return;
  track.inFlight = Math.max(0, track.inFlight - 1);
  tryReleaseTrack(clientJobId);
}

/**
 * 生成を作業エリアから切り離す (生成キューの ✕)。
 * 走っているジョブは止めない。完了はバックグラウンドで進み、結果はシナリオブック
 * (DB) にだけ残る。
 *
 * @returns track が見つかって切り離せたら true。リロード等で track が失われていたら false
 *   (呼び出し側はキューの行だけ消す)。
 */
export function detachTrack(clientJobId: string): boolean {
  const track = tracks.get(clientJobId);
  if (!track) return false;
  if (track.detached) return true;
  track.detached = true;
  // 復帰キー・in-flight registry から外す。**他の track のキーは消さない**
  // (切り離した直後に投入した後発の生成が復帰対象なので)。
  if (track.serverJobId) {
    settleScenarioJob(track.serverJobId);
    try {
      if (sessionStorage.getItem(SCENARIO_JOB_STORAGE_KEY) === track.serverJobId) {
        sessionStorage.removeItem(SCENARIO_JOB_STORAGE_KEY);
      }
    } catch { /* プライベートモード等では何もしない */ }
  }
  const onDetach = track.onDetach;
  track.onDetach = undefined;
  if (onDetach) {
    try {
      onDetach();
    } catch (error) {
      console.error('[scenarioGenerationTracks] 切り離し処理に失敗しました:', error);
    }
  }
  notify();
  return true;
}

/** attached な track を購読する React フック (生成ボタンの無効化判定に使う)。 */
export function useAttachedScenarioTrack(): ScenarioGenerationTrack | null {
  return useSyncExternalStore(subscribe, getAttachedSnapshot, getAttachedSnapshot);
}

/** テスト専用: レジストリを空にする。
 *  pendingEventProbe は消さない (本番モジュールが読み込み時に登録した probe を消すと、
 *  以降 tryRelease の条件が判定できず track が一切解放されなくなるため)。 */
export function __resetScenarioGenerationTracksForTest(): void {
  tracks.clear();
  attachedSnapshot = null;
  for (const listener of listeners) listener();
}
